import { Dayjs } from 'dayjs';
import { storeToRefs } from 'pinia';
import { computed, Ref } from 'vue';
import { useDeviceStore } from '#src/store/device';
import { useDayjs } from 'composables/dayjs';

export function useDeviceSim(serial: Ref<string>) {
  const deviceStore = useDeviceStore();
  const {
    devices,
    devicesLoading,
    simDataLoading,
    simStatuses,
    simUsage: allSimUsage,
    simUsageLoading,
  } = storeToRefs(deviceStore);

  const { dayjs } = useDayjs();

  const device = computed(() => devices.value[serial.value]);

  const isLoadingDevice = computed(() => devicesLoading.value[serial.value]);

  const isLoadingSimData = computed(() => simDataLoading.value[serial.value]);

  const isLoadingSimUsage = computed(() => simUsageLoading.value[serial.value]);

  const isLoading = computed(() => isLoadingDevice.value || isLoadingSimData.value || isLoadingSimUsage.value);

  const hasSimUsageBeyondTransmission = computed(() => {
    if (!device.value) {
      return false;
    }
    return simLastCommunication.value?.isAfter(dayjs(device.value.lastTransmission));
  });

  const noUsageInLastSevenDays = computed(
    () => !isLoading.value && !simHasCommunication.value && !device.value.isRaven
  );

  const hasBadRavenSdCardStatus = computed(() => device.value.isRaven && device.value.ravenSdCardStatus !== 'INSERTED');

  const simHasCommunication = computed(() => Boolean(simUsage.value.length) && simLastCommunication.value !== null);

  const simLastCommunication = computed(() => {
    const { dayjs } = useDayjs();
    let lastCommunication = null as Dayjs | null;

    simUsage.value.forEach((usage) => {
      const { date, total } = usage;
      if (total && (lastCommunication === null || dayjs.utc(date).isAfter(lastCommunication))) {
        lastCommunication = dayjs(date);
      }
    });

    return lastCommunication;
  });

  const simStatus = computed(() => simStatuses.value[serial.value] || 'unknown');

  const simUsage = computed(() => allSimUsage.value[serial.value] || []);

  async function getSim() {
    return deviceStore.getSim({
      serial: serial.value,
      sim: device.value.sim,
      source: device.value.simSource,
    });
  }

  async function getSimUsage() {
    return deviceStore.getSimUsage({
      serial: serial.value,
      sim: device.value.sim,
      source: device.value.simSource,
    });
  }

  function init() {
    return deviceStore.initBareDevice(serial.value);
  }

  return {
    device,
    getSim,
    getSimUsage,
    hasBadRavenSdCardStatus,
    hasSimUsageBeyondTransmission,
    init,
    isLoading,
    noUsageInLastSevenDays,
    simHasCommunication,
    simLastCommunication,
    simStatus,
    simUsage,
  };
}
